<template>
  <div v-if="mounted" class="profile-dropdown-wrapper">
    <VaDropdown
      :key="dropdownKey"
      v-model="isShown"
      :offset="[9, 0]"
      class="profile-dropdown"
      stick-to-edges
      teleport-target="body"
    >
      <template #anchor>
        <VaButton preset="secondary" color="textPrimary">
          <span class="profile-dropdown__anchor min-w-max">
            <VaAvatar :size="32" color="warning"> 😍 </VaAvatar>
          </span>
        </VaButton>
      </template>

      <VaDropdownContent
        v-show="isShown"
        class="profile-dropdown__content md:w-60 px-0 py-4 w-full"
        :style="{ '--hover-color': hoverColor }"
      >
        <template v-for="(group, index) in options" :key="`group-${index}`">
          <VaList v-if="group.list?.length">
            <header v-if="group.name" class="uppercase text-[var(--va-secondary)] opacity-80 font-bold text-xs px-4">
              {{ t(`user.${group.name}`) }}
            </header>

            <template v-for="item in group.list" :key="item.name">
              <VaButton
                v-if="item.name === 'logout'"
                class="menu-item px-4 text-base h-8 w-full justify-start"
                color="danger"
                preset="plain"
                @click="handleLogout"
              >
                <VaIcon :name="item.icon" class="pr-1" color="danger" />
                {{ t(`user.${item.name}`) }}
              </VaButton>

              <VaListItem
                v-else
                class="menu-item px-4 text-base cursor-pointer h-8"
                v-bind="resolveLinkAttribute(item)"
              >
                <VaIcon :name="item.icon" class="pr-1" color="secondary" />
                {{ t(`user.${item.name}`) }}
              </VaListItem>
            </template>

            <VaListSeparator v-if="group.separator" class="mx-3 my-2" />
          </VaList>
        </template>
      </VaDropdownContent>
    </VaDropdown>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed, onMounted, nextTick } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useColors } from 'vuestic-ui'
import API from '../../../../services/api'
import { resetDynamicRoutes } from '../../../../router/injectGroupRoutes'
import { useAccessStore } from '../../../../stores/access'

const router = useRouter()
const { t } = useI18n()
const { colors, setHSLAColor } = useColors()

const hoverColor = computed(() => setHSLAColor(colors.focus, { a: 0.1 }))
const isShown = ref(false)
const mounted = ref(false)
const dropdownKey = computed(() => `dropdown-${mounted.value}`)
const accessStore = useAccessStore()

onMounted(() => {
  nextTick(() => {
    mounted.value = true
  })
})

type ProfileListItem = {
  name: string
  to?: string
  href?: string
  icon: string
}

type ProfileOptions = {
  name: string
  separator: boolean
  list: ProfileListItem[]
}

const props = withDefaults(defineProps<{ options?: ProfileOptions[] }>(), {
  options: () => [
    {
      name: '',
      separator: false,
      list: [{ name: 'logout', icon: 'mso-logout' }],
    },
  ],
})

const resolveLinkAttribute = (item: ProfileListItem) => {
  if (item.to) return { to: { name: item.to } }
  if (item.href) return { href: item.href, target: '_blank' }
  return {}
}

const handleLogout = async () => {
  try {
    const token = localStorage.getItem('token') || sessionStorage.getItem('token')

    await API.post(
      '/logout',
      {},
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    )

    // 🧹 Bersihkan route dinamis dulu
    resetDynamicRoutes()

    // 🔒 Hapus semua token dan flag inject
    localStorage.removeItem('token')
    sessionStorage.removeItem('token')
    localStorage.setItem('routesInjected', 'false')
    sessionStorage.setItem('routesInjected', 'false')
    accessStore.setPermissions([])
    localStorage.removeItem('user_permissions')

    // 🔁 Redirect ke login
    router.push({ path: '/login' })
  } catch (error) {
    console.error('Logout failed:', error)
  }
}
</script>

<style lang="scss">
.profile-dropdown {
  cursor: pointer;

  &__content {
    .menu-item:hover {
      background: var(--hover-color);
    }
  }

  &__anchor {
    display: inline-block;
  }
}
</style>
